import { useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import type { MarkupPhoto } from './scanPhotoOptions';

const SKELETON_TILES = 8;
const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4';

interface ScanPhotoGridProps {
    photos: MarkupPhoto[];
    onPick: (photo: MarkupPhoto) => void;
    /** Verb before the photo label in each tile's accessible name. */
    pickVerb?: string;
}

function PhotoTile({
    photo,
    onPick,
    pickVerb
}: {
    photo: MarkupPhoto;
    onPick: (photo: MarkupPhoto) => void;
    pickVerb: string;
}) {
    const [broken, setBroken] = useState(false);
    return (
        <button
            type="button"
            onClick={() => onPick(photo)}
            aria-label={`${pickVerb} ${photo.label}`}
            className="group flex flex-col gap-2 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
        >
            <span className="relative block w-full aspect-[4/3] overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200 group-hover:ring-2 group-hover:ring-gray-900 transition-shadow">
                {broken ? (
                    <span className="absolute inset-0 flex items-center justify-center text-gray-400">
                        <PhotoIcon className="w-8 h-8" />
                    </span>
                ) : (
                    <img
                        src={photo.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={() => setBroken(true)}
                        className="w-full h-full object-cover"
                    />
                )}
            </span>
            <span className="text-sm font-medium text-gray-700 truncate">{photo.label}</span>
        </button>
    );
}

function ScanPhotoGrid({ photos, onPick, pickVerb = 'Mark up' }: ScanPhotoGridProps) {
    return (
        <div className={GRID}>
            {photos.map((photo) => (
                <PhotoTile key={photo.key} photo={photo} onPick={onPick} pickVerb={pickVerb} />
            ))}
        </div>
    );
}

function ScanPhotoGridSkeleton() {
    return (
        <div className={GRID} role="status" aria-label="Loading photos">
            {Array.from({ length: SKELETON_TILES }, (_, index) => (
                <div key={index} className="flex flex-col gap-2">
                    <div className="w-full aspect-[4/3] rounded-xl bg-gray-200 animate-pulse" />
                    <div className="h-4 w-2/3 rounded bg-gray-200 animate-pulse" />
                </div>
            ))}
        </div>
    );
}

export { ScanPhotoGrid, ScanPhotoGridSkeleton };
